import React, { useState, useEffect } from 'react';
import './CouponPage.css';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FiPlusCircle } from 'react-icons/fi';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi';

const EMPTY_FORM = { name: '', value: '', status: '1' };

function CouponPage({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const selectedStoreRef = selectedRestaurant?.reference || '';

  const [coupons, setCoupons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({});
  const [addFormErrors, setAddFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [goToPage, setGoToPage] = useState('');

  const itemsPerPage = 10;

  const handleNavClick = (item) => {
    if (onNavigate) onNavigate(getRouteFromItem(item));
  };

  // ── Fetch coupons ──
  useEffect(() => {
    if (!selectedStoreRef) return;
    const timer = setTimeout(() => {
      fetchCoupons();
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timer);
  }, [selectedStoreRef, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCoupons = async () => {
    setIsLoading(true);
    try {
      const url = searchTerm
        ? buildApiUrl(`/coupon/search?storeRef=${selectedStoreRef}&searchTerm=${encodeURIComponent(searchTerm)}&fuzzy=true`)
        : buildApiUrl(`/coupon/list?storeRef=${selectedStoreRef}`);
      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200) {
        setCoupons(data.data || []);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to fetch coupons', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Pagination ──
  const totalPages = Math.ceil(coupons.length / itemsPerPage);
  const paginatedCoupons = coupons.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const showingFrom = coupons.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, coupons.length);

  // ── Add ──
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
    setAddFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (form) => {
    const errors = {};
    if (!form.name?.trim()) errors.name = 'Name is required.';
    if (form.value === '' || form.value === null || form.value === undefined) errors.value = 'Value is required.';
    else if (isNaN(Number(form.value)) || Number(form.value) < 0) errors.value = 'Please enter a valid positive number.';
    return errors;
  };

  const handleAddCoupon = async () => {
    const errors = validateForm(addForm);
    setAddFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!selectedStoreRef) {
      notify('No store selected. Cannot add coupon.', 'error');
      return;
    }

    try {
      
      const res = await fetch(buildApiUrl(`/coupon/create?storeRef=${selectedStoreRef}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          // code: addForm.code.trim(),
          name: addForm.name.trim(),
          value: Number(addForm.value),
          status: Number(addForm.status),
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowAddModal(false);
        setAddForm(EMPTY_FORM);
        fetchCoupons();
      } else {
        notify('Failed to add coupon: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to add coupon: ' + err.message, 'error');
    }
  };

  // ── Edit ──
  const handleEdit = (coupon) => {
    setEditForm({ ...coupon, status: String(coupon.status) });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    setEditFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSaveEdit = async () => {
    const errors = validateForm(editForm);
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const res = await fetch(buildApiUrl(`/coupon/update`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          reference: editForm.reference,
          name: editForm.name.trim(),
          value: Number(editForm.value),
          status: Number(editForm.status),
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowEditModal(false);
        fetchCoupons();
      } else {
        notify('Failed to update coupon: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to update coupon: ' + err.message, 'error');
    }
  };

  // ── Delete ──
  const handleDelete = async (coupon) => {
    if (await confirm(`Are you sure you want to delete coupon "${coupon.code}"?`)) {
      try {
        const res = await fetch(buildApiUrl(`/coupon/delete`), {
          method: 'POST',          
          body: JSON.stringify({ reference: coupon.reference }),
          headers:{'Content-Type': 'application/json', ...getAuthHeaders()} 
        });
        const data = await res.json();
        if (data.code === 200) {
          fetchCoupons();
        } else {
          notify('Failed to delete coupon: ' + (data.msg || 'Unknown error'), 'error');
        }
      } catch (err) {
        notify('Failed to delete coupon: ' + err.message, 'error');
      }
    }
  };

  // ── Pagination buttons ──
  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Previous</button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    buttons.push(<span key="dots-start" className="pg-dots" style={{ visibility: start > 1 ? 'visible' : 'hidden' }}>...</span>);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    buttons.push(<span key="dots-end" className="pg-dots" style={{ visibility: end < totalPages ? 'visible' : 'hidden' }}>...</span>);
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</button>
    );
    return buttons;
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="couponpage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Coupon' ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="coupon-content">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">Coupon Management</span>
        </div>
        <h1 className="admin-page-title">Coupon Management</h1>
        <p className="admin-page-subtitle">Manage coupons — create, edit, enable/disable and remove.</p>

        {/* Table */}
        <HasPermi permission={['coupon:list', 'coupon:search']} any>
          <div className="coupon-table-card">
            <div className="coupon-table-header">
              <h2 className="coupon-table-title">Coupon List</h2>
              <div className="coupon-table-actions">
                <div className="search-box">
                  <input type="text" placeholder="Search by code or name..."
                    value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>
                <HasPermi permission="coupon:create">
                  <button className="add-coupon-btn"
                    onClick={() => { setShowAddModal(true); setAddForm(EMPTY_FORM); setAddFormErrors({}); }}>
                    <FiPlusCircle /> Add Coupon
                  </button>
                </HasPermi>
              </div>
            </div>

            <table className="coupon-table">
              <thead>
                <tr>
                  <th>CODE</th>
                  <th>NAME</th>
                  <th>VALUE</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
                ) : paginatedCoupons.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No coupons found.</td></tr>
                ) : paginatedCoupons.map(coupon => (
                  <tr key={coupon.id}>
                    <td>{coupon.code}</td>
                    <td>{coupon.name}</td>
                    <td>{Number(coupon.value).toFixed(2)}</td>
                    <td>
                      <span className={`status-badge ${coupon.status === 1 ? 'enabled' : 'disabled'}`}>
                        {coupon.status === 1 ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>{coupon.createdAt}</td>
                    <td className="actions"   >
                      <HasPermi permission="coupon:update">
                        <button className="edit-btn" onClick={() => handleEdit(coupon)}><FaEdit /></button>
                      </HasPermi>
                      <HasPermi permission="coupon:delete">
                        <button className="delete-btn" onClick={() => handleDelete(coupon)}><FaTrash /></button>
                      </HasPermi>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {coupons.length} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input className="pg-goto-input" type="number" min="1" max={totalPages} value={goToPage}
                  onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } } }}
                  placeholder="" />
                <button className="pg-btn" onClick={() => { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } }}>Go</button>
              </div>
            </div>
          </div>
        </HasPermi>

        <HasPermi
          permission={['coupon:list', 'coupon:search']}
          any
          fallback={<div className="no-permission-message"><p>You don't have permission to view coupons.</p></div>}
        />
      </main>

      {/* Add Modal */}
      <HasPermi permission="coupon:create">
        {showAddModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowAddModal(false)}>×</button>
                <h2 className="admin-modal-title">Add Coupon</h2>
                <p className="admin-modal-subtitle">Create a new coupon. Fill in all required fields.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Name <span className="required">*</span></label>
                  <input className={addFormErrors.name ? 'input-error' : ''} name="name" value={addForm.name}
                    onChange={handleAddInputChange} placeholder="e.g. Summer Promotion" />
                  {addFormErrors.name && <p className="field-error">{addFormErrors.name}</p>}
                </div>
                <div className="form-field">
                  <label>Value <span className="required">*</span></label>
                  <input className={addFormErrors.value ? 'input-error' : ''} name="value" type="number" step="0.01" min="0"
                    value={addForm.value} onChange={handleAddInputChange} placeholder="e.g. 10.00" />
                  {addFormErrors.value && <p className="field-error">{addFormErrors.value}</p>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={addForm.status} onChange={handleAddInputChange}>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleAddCoupon}>Add Coupon</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>

      {/* Edit Modal */}
      <HasPermi permission="coupon:update">
        {showEditModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowEditModal(false)}>×</button>
                <h2 className="admin-modal-title">Edit Coupon</h2>
                <p className="admin-modal-subtitle">Update the coupon information.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Code</label>
                  <input value={editForm.code || ''} disabled className="disabled-input" />
                </div>
                <div className="form-field">
                  <label>Name <span className="required">*</span></label>
                  <input className={editFormErrors.name ? 'input-error' : ''} name="name" value={editForm.name || ''}
                    onChange={handleEditInputChange} placeholder="e.g. Summer Promotion" />
                  {editFormErrors.name && <p className="field-error">{editFormErrors.name}</p>}
                </div>
                <div className="form-field">
                  <label>Value <span className="required">*</span></label>
                  <input className={editFormErrors.value ? 'input-error' : ''} name="value" type="number" step="0.01" min="0"
                    value={editForm.value ?? ''} onChange={handleEditInputChange} placeholder="e.g. 10.00" />
                  {editFormErrors.value && <p className="field-error">{editFormErrors.value}</p>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={editForm.status ?? '1'} onChange={handleEditInputChange}>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleSaveEdit}>Save Changes</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default CouponPage;
